import PollingLoader from './pollingLoader'
import Renderable from './renderable'
import Slider from 'js/render/slider'
import {Object3D as ThreeObject3D} from 'three'
import {defaultViewerConfig} from './config'

const stlConfigs = defaultViewerConfig()

// Diff driver
class ThreeDDiff extends ThreeObject3D {
  constructor(viewer, object1, object2, ready) {
    super()
    this.viewer = viewer
    this.slider = new Slider(
      {
        mouseDown: () => this.viewer.controls.modes.mouse.hold(),
        slide: percentage => this.adjustMaterialOpacity(percentage / 100),
        mouseUp: () => this.viewer.controls.modes.mouse.release()
      },
      {percentage: 100}
    )

    this.viewer.addEventListener('resize', this.slider.setDragger)

    const partLoader = new PollingLoader({status: this.viewer.status})
    partLoader.load(
      {
        added: this.viewer.$().data('added'),
        removed: this.viewer.$().data('removed'),
        unchanged: this.viewer.$().data('unchanged')
      },
      (error, loader) => {
        if (error) {
          return this.viewer.dispatchEvent({type: 'error', data: event.data != null ? event.data : 'Load error'})
        }
        const {parts} = loader

        // Diff mode definitions
        this.diffModes = {
          highlight: new Renderable(parts.unchanged.geom, {
            center: true,
            name: 'highlight',
            mode: 'wireframe',
            color: 0x555555,
            specular: 0x000000,
            parts: [
              new Renderable(parts.added.geom, {name: 'added', center: false, color: 0x00ff00}),
              new Renderable(parts.removed.geom, {name: 'removed', center: false, color: 0xff0000})
            ]
          }),

          slider: new Renderable(parts.unchanged.geom, {
            center: true,
            name: 'slider',
            parts: [
              new Renderable(parts.added.geom, {
                name: 'added',
                center: false,
                transparent: true,
                color: stlConfigs.color
              }),
              new Renderable(parts.removed.geom, {
                name: 'removed',
                center: false,
                transparent: true,
                opacity: 0,
                color: stlConfigs.color
              })
            ],
            color: stlConfigs.color
          })
        }

        // Add all the modes we defined
        for (const name of Object.keys(this.diffModes)) {
          const mode = this.diffModes[name]
          this.add(mode)
        }

        // Select one
        this.switchMode('highlight')
        ready(this)
      }
    )
  }

  opacitySlider() {
    this.slider.show()
    for (const name of Object.keys(this.diffModes)) {
      const mode = this.diffModes[name]
      mode.toggle(false)
    }
    this.diffModes.slider.toggle(true)
  }

  removeOpacitySlider() {
    this.slider.hide()
    this.diffModes.slider.toggle(false)
  }

  adjustMaterialOpacity(percentage) {
    const limit = 0.03
    if (percentage > 1 - limit) {
      this.diffModes.slider.getObjectByName('removed').toggle(false)
      this.diffModes.slider.getObjectByName('added').toggle(true)
      this.diffModes.slider.getObjectByName('added').updateMaterials({opacity: 1})
    } else if (percentage < limit) {
      this.diffModes.slider.getObjectByName('added').toggle(false)
      this.diffModes.slider.getObjectByName('removed').toggle(true)
      this.diffModes.slider.getObjectByName('removed').updateMaterials({opacity: 1})
    } else {
      this.diffModes.slider.toggle(true)
      this.diffModes.slider.getObjectByName('removed').updateMaterials({opacity: 1 - percentage})
      this.diffModes.slider.getObjectByName('added').updateMaterials({opacity: percentage})
    }
  }

  wireframe() {
    for (const name of Object.keys(this.diffModes)) {
      const mode = this.diffModes[name]
      mode.toggle(false)
    }
    this.diffModes.highlight.toggle(true)
  }

  removeWireframe() {
    this.diffModes.highlight.toggle(false)
  }

  switchMode(mode) {
    if (!mode) {
      mode = 'highlight'
    }
    switch (mode) {
      case 'highlight':
        this.removeOpacitySlider()
        return this.wireframe()
      case 'slider':
        this.removeWireframe()
        return this.opacitySlider()
    }
  }
}

export default ThreeDDiff
